import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { ordersApi } from '@/services/orders'

const parseAmount = (amount?: string | number | null) => {
  if (typeof amount === 'number') return Number.isFinite(amount) ? amount : 0
  if (typeof amount === 'string') {
    const parsed = Number(amount.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatMoney = (amount: number, currency?: string | null) => {
  const marker = String(currency ?? '').trim()
  if (/^[A-Za-z]{3}$/.test(marker)) return formatCurrency(amount, marker.toUpperCase())
  return formatCurrency(amount)
}

type PaymentState = 'paid' | 'failed' | 'pending'

const resolvePaymentState = (order: { is_paid?: boolean | null; payment_status?: string | number | null } | undefined): PaymentState => {
  if (!order) return 'pending'
  if (order.is_paid) return 'paid'

  const normalized = String(order.payment_status ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
  if (!normalized) return 'pending'
  if (normalized.includes('success') || normalized.includes('verified') || normalized === '3' || normalized === '1') return 'paid'
  if (normalized.includes('fail') || normalized.includes('cancel') || normalized.includes('reject') || normalized.includes('declin')) return 'failed'
  return 'pending'
}

const formatPaymentStatusLabel = (status?: string | number | null) => {
  if (status == null || status === '') return '-'
  const text = String(status).trim()
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams()
  const referenceId = searchParams.get('reference_id') || ''
  const paymentStatusParam = searchParams.get('paymentStatus')

  const orderQuery = useQuery({
    queryKey: ['order-confirmation', referenceId],
    queryFn: () => ordersApi.getOrderConfirmationDetail(referenceId),
    enabled: Boolean(referenceId),
    retry: false,
  })

  const order = orderQuery.data
  const paymentState = resolvePaymentState(order)
  const currency = order?.currency ?? undefined
  const finalTotal = useMemo(() => parseAmount(order?.final_total ?? order?.total), [order])

  if (!referenceId) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardHeader><CardTitle>Order reference missing</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No order reference was provided in the link.
        </CardContent>
      </Card>
    )
  }

  if (orderQuery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    )
  }

  if (orderQuery.isError || !order) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardHeader><CardTitle>Unable to load order</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {orderQuery.isError ? getErrorMessage(orderQuery.error) : 'This order could not be found.'}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            {paymentState === 'paid' ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            ) : paymentState === 'failed' ? (
              <XCircle className="h-6 w-6 text-rose-600" />
            ) : (
              <Clock className="h-6 w-6 text-amber-600" />
            )}
            {paymentState === 'paid' ? 'Payment successful' : paymentState === 'failed' ? 'Payment failed' : 'Payment pending'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Order reference</span>
            <span className="font-medium">#{order.reference_id || order.id}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Payment status</span>
            <Badge
              variant="outline"
              className={
                paymentState === 'paid'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : paymentState === 'failed'
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-amber-200 bg-amber-50 text-amber-700'
              }
            >
              {formatPaymentStatusLabel(order.payment_status) !== '-' ? formatPaymentStatusLabel(order.payment_status) : paymentState}
            </Badge>
          </div>
          {order.payment_mode_name && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Payment method</span>
              <span>{order.payment_mode_name}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Order total</span>
            <span className="font-semibold">{formatMoney(finalTotal, currency)}</span>
          </div>
          {paymentStatusParam === 'true' && paymentState !== 'paid' && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>The latest order status is shown above and may differ from the redirect result.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Order details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid gap-2 sm:grid-cols-2">
            {(order.full_name || order.customer_name) && (
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{order.full_name || order.customer_name}</p>
              </div>
            )}
            {(order.email || order.customer_email) && (
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium">{order.email || order.customer_email}</p>
              </div>
            )}
            {(order.mobile || order.customer_phone) && (
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="font-medium">{order.mobile || order.customer_phone}</p>
              </div>
            )}
            {order.order_time && (
              <div>
                <p className="text-xs text-muted-foreground">Order time</p>
                <p className="font-medium">{order.order_time}</p>
              </div>
            )}
          </div>

          {order.order_items.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">Items</p>
                {order.order_items.map((item, index) => (
                  <div key={item.id ?? index} className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">{item.product_variant_name || item.product_name || `Item ${index + 1}`}</p>
                      <p className="text-xs text-muted-foreground">Qty {item.qty ?? 1} x {formatMoney(parseAmount(item.unit_price), currency)}</p>
                    </div>
                    <span className="font-semibold">{formatMoney(parseAmount(item.final_price), currency)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {order.totals.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                {order.totals.map((line, index) => (
                  <div key={`${line.label}-${index}`} className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">{line.label}</span>
                    <span className="font-medium">{formatMoney(parseAmount(line.amount), line.currency ?? currency)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

